import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { 
  Modal, 
  Box, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  Typography,
  Select,
  MenuItem,
  Card,
  CircularProgress,
  CardContent,
  styled
} from '@mui/material';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import { useAuthToken } from "../TokenContext";
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';

const StyledCard = styled(Card)(({ theme }) => ({
  cursor: 'pointer',
  transition: 'box-shadow 0.3s',
  width: '300px',
  height: '250px',
 marginTop:"4.5rem",
  '&:hover': {
    boxShadow: theme.shadows[4]
  }
}));

const LoadingContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '140px'
});

const StyledModalBox = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(3),
  width: '75%',
  maxHeight: '80vh',
  overflowY: 'auto'
}));

const CardEpics = ({ initialPodIds, selectedSprint }) => {
  const [epicsData, setEpicsData] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [displayedEpics, setDisplayedEpics] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState('all');

  const token = useAuthToken();
  const intl = useIntl();
  const { user } = useSelector((state) => state.auth);

  // Memoize the updateFilteredEpics function to prevent re-creation on every render
  const updateFilteredEpics = useCallback(() => {
    if (!epicsData) {
      setDisplayedEpics([]);
      return;
    }

    if (selectedStatus === 'open') {
      setDisplayedEpics(epicsData.open_epics_details || []);
    } else if (selectedStatus === 'completed') {
      setDisplayedEpics(epicsData.completed_epics_details || []);
    } else {
      // "all" is selected
      const allEpics = [
        ...(epicsData.open_epics_details || []), 
        ...(epicsData.completed_epics_details || [])
      ];
      setDisplayedEpics(allEpics);
    }
  }, [epicsData, selectedStatus]);

  // Memoize fetchEpicsData to prevent unnecessary re-creation
  const fetchEpicsData = useCallback(async () => {
    if (!token || !user?.data?.[0]) {
      setIsLoading(false);
      return;
    }

    try {
      let url;
      let payload = {};

      const type = (user.data[0].type || "").toLowerCase();
      if (type === "stakeholder") {
        url = process.env.REACT_APP_STAKEHOLDER_EPICSCOUNTMETRICSSTAKEHOLDER;
        let orgCode = user.data[0].client_code;
        if (orgCode && !orgCode.includes('#')) orgCode = `CLIENT#${orgCode}`;

        payload = {
          org_code: orgCode,
          pod_id: initialPodIds || [],
          sprint_name: selectedSprint || ''
        };
      } else if (type === "talent" || type === "candidate") {
        url = process.env.REACT_APP_TALENT_EPICSCOUNTMETRICSTALENT;
        payload = {
          c_id: user.data[0].c_id || user.data[0].emp_id,
          pod_id: initialPodIds || [],
          sprint_name: selectedSprint || ''
        };
      } else {
        // Default to manager
        url = process.env.REACT_APP_CLIENT_EPCCOUNTMETRICSMANAGER;
        payload = {
          manager_id: user.data[0].emp_id,
          pod_id: initialPodIds || [],
          sprint_name: selectedSprint || ''
        };
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
             'Authorization': type === "stakeholder" ? token : `Bearer ${token}`,
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      
      if (data?.body) {
        setEpicsData(data.body);
      } else {
        setEpicsData(null);
        setDisplayedEpics([]);
      }
    } catch (error) {
      console.error('Error fetching epics data:', error);
      setEpicsData(null);
      setDisplayedEpics([]);
    } finally {
      setIsLoading(false);
    }
  }, [token, user, initialPodIds, selectedSprint]);

  // Fetch data when dependencies change
  useEffect(() => {
    setIsLoading(true);
    fetchEpicsData();
  }, [fetchEpicsData]);

  // Update filtered epics when status selection changes or when data is loaded
  useEffect(() => {
    updateFilteredEpics();
  }, [updateFilteredEpics]);

  const getDisplayData = () => {
    if (!epicsData) return { open: 0, completed: 0 };

    return {
      open: epicsData.open_epics || 0,
      completed: epicsData.completed_epics || 0
    };
  };

  const handleStatusChange = (event) => {
    setSelectedStatus(event.target.value);
  };

  return (
    <Box sx={{ height: '100%' }}>
      <Box 
        display="flex" 
        flexDirection="column" 
        alignItems="center"
        sx={{ py: 3 }}
      >
        <Typography 
          variant="h5" 
          gutterBottom 
          sx={{ fontWeight: 'medium', mb: 3 }}
        >
          {/* {intl.formatMessage({ id: 'Epics' }, { defaultMessage: 'Epics' })} */}
          {intl.formatMessage({ id: "epics" })}
        </Typography>
       

        <StyledCard onClick={() => setModalOpen(true)}>
          <CardContent sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" mb={2}>
              <TaskAltIcon 
                style={{ 
                  width: 32, 
                  height: 32, 
                  marginRight: 12, 
                  color: '#9370DB'
                }} 
              />
              <Typography variant="h6" sx={{
                fontWeight: 600,
                letterSpacing: '0.5px',
                background: 'linear-gradient(to right, #9370DB, #8A2BE2)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
               {intl.formatMessage({ id: "epics" })}
              </Typography>
            </Box>
            {isLoading ? (
              <LoadingContainer>
                <CircularProgress />
              </LoadingContainer>
            ) : (
              <Box sx={{ mt: 5.5 }}>
                <Box 
                  display="flex" 
                  justifyContent="space-between" 
                  alignItems="center"
                  mb={1.5}
                >
                  <Typography color="text.secondary">TO DO:</Typography>
                  <Typography 
                    fontWeight="bold"
                    sx={{ ml: 2 }}
                  >
                    {getDisplayData().open}
                  </Typography>
                </Box>
                <Box 
                  display="flex" 
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography color="text.secondary">DONE:</Typography>
                  <Typography 
                    fontWeight="bold"
                    sx={{ ml: 2 }}
                  >
                    {getDisplayData().completed}
                  </Typography>
                </Box>
              </Box>
            )}
          </CardContent>
        </StyledCard>
      </Box>

      {modalOpen && (
        <Modal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          aria-labelledby="epics-modal-title"
        >
          <StyledModalBox>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography 
                id="epics-modal-title" 
                variant="h6" 
                sx={{ fontWeight: 'medium' }}
              >
                Epic Details
              </Typography>
              
              <Select
                value={selectedStatus}
                onChange={handleStatusChange}
                size="small"
                sx={{ minWidth: 200 }}
              >
                <MenuItem value="all">
                  {/* {intl.formatMessage({ id: 'allEpics' }, { defaultMessage: 'All Epics' })} */}
                  All Epics
                </MenuItem>
                <MenuItem value="open">
                  {/* {intl.formatMessage({ id: 'openEpics' }, { defaultMessage: 'Open Epics' })} */}
                  Open Epics
                </MenuItem>
                <MenuItem value="completed">
                  {/* {intl.formatMessage({ id: 'completedEpics' }, { defaultMessage: 'Completed Epics' })} */}
                  Completed Epics
                </MenuItem>
              </Select>
            </Box>
            
            <TableContainer 
              component={Paper}
              sx={{ maxHeight: '60vh' }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell 
                      sx={{ 
                        bgcolor: 'primary.light',
                        fontWeight: 'medium'
                      }}
                    >
                      {/* {intl.formatMessage({ id: 'epicKey' }, { defaultMessage: 'Epic Key' })} */}
                      Epic Key
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        bgcolor: 'primary.light',
                        fontWeight: 'medium'
                      }}
                    >
                      {/* {intl.formatMessage({ id: 'epicSummary' }, { defaultMessage: 'Epic Summary' })} */}
                      Epic Summary
                    </TableCell>
                    <TableCell 
                      sx={{ 
                        bgcolor: 'primary.light',
                        fontWeight: 'medium'
                      }}
                    >
                      {/* {intl.formatMessage({ id: 'status' }, { defaultMessage: 'Status' })} */}
                      Status
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayedEpics.map((epic, index) => (
                    <TableRow 
                      key={epic.key || index}
                      sx={{ 
                        '&:nth-of-type(odd)': { 
                          bgcolor: 'action.hover' 
                        },
                        '&:hover': {
                          bgcolor: 'action.selected'
                        }
                      }}
                    >
                      <TableCell>{epic.key}</TableCell>
                      <TableCell>{epic.summary}</TableCell>
                      <TableCell>{epic.status}</TableCell>
                    </TableRow>
                  ))}
                  {displayedEpics.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        <Typography variant="body2" sx={{ py: 2 }}>
                          {intl.formatMessage(
                            { id: 'noEpicsFound' }, 
                            { defaultMessage: 'No epics found for the selected filter' }
                          )}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </StyledModalBox>
        </Modal>
      )}
    </Box>
  );
};

CardEpics.propTypes = {
  initialPodIds: PropTypes.array.isRequired,
  selectedSprint: PropTypes.string
};

export default CardEpics;